// ════════════════════════════════════════════════════════════════════
// ranges.js — selectable date-range presets for the history filters.
// ════════════════════════════════════════════════════════════════════
// "1D" = today only (the single current calendar day) — the default landing
// view for Overview and Insights. "1DY" = yesterday only. "1W" = last 7 days.
// This is the canonical, unified preset list — identical to the mobile app.
export const RANGES = [
  {id:"1D",l:"Today"},
  {id:"1DY",l:"Yday"},
  {id:"1W",l:"7D"},
  {id:"1M",l:"1M"},
  {id:"3M",l:"3M"},
  {id:"6M",l:"6M"},
  {id:"1Y",l:"1Y"},
  {id:"2Y",l:"2Y"},
  {id:"5Y",l:"5Y"},
  {id:"ALL",l:"All"},
];

// Day-counts for the rolling presets (PORTAL convention: cutoff = today - days,
// NOT days-1). Keep in sync with the mobile app + the canonical filter spec.
const DAY_COUNTS = {"1W":7,"1M":31,"3M":92,"6M":183,"1Y":365,"2Y":730,"5Y":1825};

const ymdL = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;

// ── rangeWindow — the SINGLE source of truth for the selected window. ──────
// One formula used for BOTH row-selection (cutoff/rangeEnd) AND the CPC period
// window (winFrom/winTo), given local "today". See the canonical filter spec.
//   cutoff   : Date  — inclusive lower bound (startOfDay)
//   rangeEnd : Date  — inclusive upper bound (endOfDay)
//   winFrom  : string|null — ymd(cutoff); null for "All"
//   winTo    : string|null — ymd(min(rangeEnd, today)); null only when no window
// CPC convention: count CPC only for current + past days, never future days of
// a range — hence winTo is capped at today.
export function rangeWindow(range, customFrom, customTo) {
  const today = new Date();
  const todayY = ymdL(today);
  const startOfDay = d => { const x = new Date(d); x.setHours(0,0,0,0); return x; };
  const endOfDay   = d => { const x = new Date(d); x.setHours(23,59,59,999); return x; };
  let cutoff, rangeEnd;

  if (range === "CUSTOM" && customFrom && customTo) {
    cutoff   = new Date(customFrom + "T00:00:00");
    rangeEnd = endOfDay(new Date(customTo + "T00:00:00"));  // .999 — match the other ranges' end-of-day (and mobile)
  } else if (range === "ALL" || range === "all" || range === "All") {
    cutoff   = new Date("2000-01-01T00:00:00");  // far-past sentinel
    rangeEnd = endOfDay(today);
    // "All" = unbounded window (full entered amounts) — no winFrom.
    return { cutoff, rangeEnd, winFrom: null, winTo: todayY };
  } else if (range === "1D") {
    cutoff   = startOfDay(today);
    rangeEnd = endOfDay(today);
  } else if (range === "1DY") {
    const y = new Date(today.getTime() - 86400000);
    cutoff   = startOfDay(y);
    rangeEnd = endOfDay(y);
  } else {
    // Rolling presets (1W/1M/3M/6M/1Y/2Y/5Y): cutoff = today - days (NOT days-1).
    const days = DAY_COUNTS[range] || 9999;
    cutoff   = startOfDay(new Date(today.getTime() - days * 86400000));
    rangeEnd = endOfDay(today);
  }

  // winTo = ymd(min(rangeEnd, today)) — cap the CPC window at today.
  const winTo = ymdL(rangeEnd.getTime() < today.getTime() ? rangeEnd : today);
  return { cutoff, rangeEnd, winFrom: ymdL(cutoff), winTo };
}

export function filterHistory(history, range, customFrom, customTo) {
  if(!history?.length) return [];
  const safe = history.filter(r => r?.dateObj instanceof Date);
  // "All" returns everything (incl. far-future / undateable rows) — the window's
  // far-past cutoff would otherwise still drop nothing, but keep the fast path.
  if(range==="ALL"||range==="all"||range==="All") return safe;
  if(range==="CUSTOM" && !(customFrom && customTo)) return safe;
  // Calendar-based selection via the shared window: include a row iff
  // cutoff <= dateObj <= rangeEnd (NOT a rolling now-instant comparison).
  const { cutoff, rangeEnd } = rangeWindow(range, customFrom, customTo);
  const lo = cutoff.getTime(), hi = rangeEnd.getTime();
  return safe.filter(r => r.dateObj.getTime() >= lo && r.dateObj.getTime() <= hi);
}
